export const GradeOptions = [
    { value: null, text: '請選擇' },
    { value: 0, text: '其他' },
    { value: 1, text: '一年級' },
    { value: 2, text: '二年級' },
    { value: 3, text: '三年級' },
    { value: 4, text: '四年級' },
    { value: 5, text: '五年級' },
    { value: 6, text: '六年級' },
];

export const ENGLISH_NAME_MAX_LENGTH = 255;

export const CampRefundMethodOptions = [
    { value: 1, text: '現金退費' },
    { value: 2, text: '轉帳退費' },
];

// 收款方式，對應後端 CampPaymentMethodType（1=現金、2=轉帳）。
export const CampPaymentMethodOptions = [
    { value: 1, text: '現金結帳（至現場繳費）' },
    { value: 2, text: '轉帳（回報匯款資訊）' },
];

// 營隊訂單整體狀態，對應後端 CampOrderStatusType（10-90，顯示名同 spec §4）。
export const CampOrderStatusOptions = [
    { value: 10, text: '待付款' },
    { value: 20, text: '對帳中' },
    { value: 30, text: '對帳失敗' },
    { value: 40, text: '已付款' },
    { value: 50, text: '部分退費中' },
    { value: 60, text: '退費中' },
    { value: 70, text: '部分已退費' },
    { value: 80, text: '已退費' },
    { value: 90, text: '已取消' },
];

export const isActiveCampRefundStatus = (statusNum) => [10, 20, 30].includes(Number(statusNum));

// 訂單狀態徽章樣式：9 種狀態各有對應色，不 fallback 到未知狀態。
export const campOrderStateVariant = (statusNum) => {
    switch (Number(statusNum)) {
        case 10: return 'warning'; // 待付款
        case 20: return 'info'; // 對帳中
        case 30: return 'danger'; // 對帳失敗
        case 40: return 'success'; // 已付款
        case 50: return 'warning'; // 部分退費中
        case 60: return 'warning'; // 退費中
        case 70: return 'info'; // 部分已退費
        case 80: return 'secondary'; // 已退費
        case 90: return 'secondary'; // 已取消
        default: return 'light';
    }
};

// 訂單明細頁頂部提示只負責 presentation：可否操作仍以後端 Actions／各付款單 CanReport 為準。
// 回傳單一最高優先提示，避免 View 內散落互相競爭的狀態判斷。
export const resolveCampOrderAttention = (order, refunds = [], options = {}) => {
    if (!order) return null;

    const actions = order.Actions || {};
    const surcharges = order.Surcharges || [];
    const sortedRefunds = [...(refunds || [])].sort((a, b) => new Date(b.Create) - new Date(a.Create));
    const pendingSurcharges = surcharges.filter((s) => s.CanReport);
    const requiredActionCount = Number(!!actions.CanSubmitRefundAccount)
        + Number(!!actions.CanReport || !!actions.CanRetryPayment)
        + pendingSurcharges.length;
    const withRemainingActions = (attention) => {
        const remainingCount = Math.max(0, requiredActionCount - 1);
        return remainingCount
            ? { ...attention, description: `${attention.description} 另有 ${remainingCount} 項待處理事項，請於下方查看。` }
            : attention;
    };
    const accountRefund = sortedRefunds.find((r) =>
        r.Id === actions.RefundAccountRefundId || r.NeedRefundAccount,
    );

    if (actions.CanSubmitRefundAccount) {
        const refundId = accountRefund && accountRefund.Id
            ? accountRefund.Id
            : actions.RefundAccountRefundId;
        return withRemainingActions({
            type: 'needs-action',
            icon: options.refundsLoadError ? 'exclamation-triangle-fill' : 'exclamation-circle-fill',
            eyebrow: '需要您的處理',
            title: '尚未填寫退款帳戶資料',
            description: options.refundsLoadError
                ? '退款資料暫時無法載入，請重新讀取後填寫帳戶資料。'
                : '請完成退款帳戶資料並上傳帳戶證明，以便工作人員辦理退款。',
            actionLabel: options.refundsLoadError ? '重新讀取' : '填寫退款資料',
            actionType: options.refundsLoadError ? 'reload-refunds' : 'scroll',
            target: refundId ? `refund-account-${refundId}` : null,
        });
    }

    if (actions.CanReport) {
        const isCounterPayment = Number(order.Payment && order.Payment.PaymentMethodNum) === 1;
        return withRemainingActions({
            type: 'needs-action',
            icon: 'exclamation-circle-fill',
            eyebrow: '需要您的處理',
            title: isCounterPayment ? '請至現場完成繳費' : '請完成付款回報',
            description: isCounterPayment
                ? '此訂單採現金結帳，請於指定時間內至現場繳費，完成後由工作人員確認收款。'
                : '請依付款資訊完成繳費，並填寫匯款回報資料。',
            actionLabel: isCounterPayment ? '查看繳費資訊' : '前往付款回報',
            actionType: 'scroll',
            target: 'order-payment',
        });
    }

    if (actions.CanRetryPayment) {
        return withRemainingActions({
            type: 'needs-action',
            icon: 'exclamation-triangle-fill',
            eyebrow: '需要您的處理',
            title: '付款回報未通過',
            description: '請查看對帳失敗原因，重新建立付款資料後再進行回報。',
            actionLabel: '重新付款',
            actionType: 'retry-payment',
        });
    }

    const pendingSurcharge = pendingSurcharges[0];
    if (pendingSurcharge) {
        return withRemainingActions({
            type: 'needs-action',
            icon: 'exclamation-circle-fill',
            eyebrow: '需要您的處理',
            title: '有一筆補收款待繳',
            description: pendingSurcharge.Reason || '請完成補收款繳費與回報。',
            actionLabel: '前往繳費',
            actionType: 'pay-surcharge',
            surcharge: pendingSurcharge,
        });
    }

    if (options.refundsLoadError) {
        return {
            type: 'needs-action',
            icon: 'exclamation-triangle-fill',
            eyebrow: '資料讀取失敗',
            title: '暫時無法確認退費待辦',
            description: '退款資料未能完整載入，請重新讀取後再確認目前是否需要操作。',
            actionLabel: '重新讀取',
            actionType: 'reload-refunds',
        };
    }

    const pendingRefund = sortedRefunds.find((r) => isActiveCampRefundStatus(r.StatusNum));
    const pendingSurchargeConfirmation = surcharges.find((s) => Number(s.StatusNum) === 20);
    const orderPaymentPending = Number(order.OrderStateNum) === 20
        || Number(order.Payment && order.Payment.StatusNum) === 20;

    if (orderPaymentPending || pendingSurchargeConfirmation) {
        return {
            type: 'processing',
            icon: 'clock-fill',
            eyebrow: '工作人員處理中',
            title: pendingSurchargeConfirmation ? '補收款資料確認中' : '款項資料確認中',
            description: '已收到你的回報，目前無需進行其他操作；確認完成後將更新訂單狀態。',
            actionLabel: '查看付款資訊',
            actionType: 'scroll',
            target: pendingSurchargeConfirmation
                ? `surcharge-${pendingSurchargeConfirmation.PaymentId}`
                : 'order-payment',
        };
    }

    if (pendingRefund) {
        return {
            type: 'processing',
            icon: 'clock-fill',
            eyebrow: '工作人員處理中',
            title: '此訂單有退費申請',
            description: '退費審核或退款作業進行中，目前無需進行其他操作，完成後將通知你。',
            actionLabel: '查看退費進度',
            actionType: 'scroll',
            target: `refund-${pendingRefund.Id}`,
        };
    }

    if (Number(order.OrderStateNum) === 40) {
        return {
            type: 'completed',
            icon: 'check-circle-fill',
            eyebrow: '目前狀態',
            title: '訂單已完成付款',
            description: '目前沒有需要你處理的事項。',
        };
    }

    if ([70, 80, 90].includes(Number(order.OrderStateNum))) {
        return {
            type: 'informational',
            icon: 'info-circle-fill',
            eyebrow: '目前狀態',
            title: order.OrderState || '訂單狀態已更新',
            description: '目前沒有需要你處理的事項，可於下方查看訂單與歷程。',
        };
    }

    return {
        type: 'informational',
        icon: 'info-circle-fill',
        eyebrow: '目前狀態',
        title: order.OrderState || '訂單處理中',
        description: '目前沒有需要你處理的事項。',
    };
};

// Number(null) === 0 為 true，Grade 未選擇時不可誤判為「其他」，需先排除 null/undefined/空字串。
export const isOtherGrade = (grade) => grade !== null && grade !== undefined && grade !== '' && Number(grade) === 0;

export const GRADE_BIRTHDAY_MISMATCH_MESSAGE = '年級可能與生日不一致，請確認是否正確';

// 學年度以 7/1 起算（每年 7/1 更新年級），暑假期間（7、8 月）家長常直接填開學後的新年級；
// 需與後端 CampSchoolYear.StartMonth 保持一致。
export const SCHOOL_YEAR_START_MONTH = 7;

// 暑假（7、8 月）才顯示的年級提醒，其他月份回傳空字串。
export const getGradeSummerNotice = (date = new Date()) => {
    const month = date.getMonth() + 1;
    return month >= SCHOOL_YEAR_START_MONTH && month <= 8
        ? '目前為暑假期間，年級請填 9 月開學後的年級'
        : '';
};

// 家庭成員 / 營隊報名快照共用必填驗證，欄位與訊息比照後端 CampValidation.ValidateFamilyMemberFields。
export const validateFamilyMemberFields = (name, birthday, gender, school, grade, gradeMemo) => {
    if (!String(name || '').trim()) return '姓名為必填';
    if (!birthday) return '生日為必填';
    if (gender === null || gender === undefined || gender === '') return '性別為必填';
    if (!String(school || '').trim()) return '就讀學校為必填';
    if (grade === null || grade === undefined || grade === '') return '年級為必填';
    if (Number(grade) === 0 && !String(gradeMemo || '').trim()) return '年級為其他時，年級備註為必填';
    return '';
};

export const validateEnglishNameLength = (englishName, maxLength = ENGLISH_NAME_MAX_LENGTH, fieldName = '英文姓名') => {
    const trimmed = String(englishName || '').trim();
    if (trimmed.length > maxLength) return `${fieldName}長度不得超過 ${maxLength} 字`;
    return '';
};

// 英文姓氏、英文名字、英文別名（沿用 EnglishName 欄位）逐欄驗證，訊息比照後端 CampValidation.ValidateEnglishNameParts。
export const validateEnglishNameParts = (lastName, firstName, alias) =>
    validateEnglishNameLength(lastName, ENGLISH_NAME_MAX_LENGTH, '英文姓氏')
    || validateEnglishNameLength(firstName, ENGLISH_NAME_MAX_LENGTH, '英文名字')
    || validateEnglishNameLength(alias, ENGLISH_NAME_MAX_LENGTH, '英文別名');

// 唯讀顯示英文姓名：「{姓} {名}」，別名有值時後接「（別名）」；姓、名皆空（舊資料）時只顯示別名（EnglishName）。
export const formatEnglishName = (lastName, firstName, alias) => {
    const fullName = [lastName, firstName].map((v) => String(v || '').trim()).filter(Boolean).join(' ');
    const aliasText = String(alias || '').trim();
    if (fullName) return aliasText ? `${fullName}（${aliasText}）` : fullName;
    return aliasText;
};

// Grade = 0（其他）排除比對；僅為柔性提醒，不阻擋送出。
// 依台灣入學年齡：小一須在入學年 9/1 當天滿 6 歲，9/1 當天以前出生者出生年 + 6 入學，9/2 以後出生者晚一年入學；
// 由此算出應屆年級，與實際年級相差超過 1 個年級才提醒（容許提早入學、留級、跳級）。需與後端 CampSchoolYear.IsGradeBirthdayMismatch 保持一致。
export const isGradeBirthdayMismatch = (grade, birthday) => {
    if (grade === null || grade === undefined || grade === '') return false;
    const gradeNum = Number(grade);
    if (Number.isNaN(gradeNum) || gradeNum === 0) return false;
    if (!birthday) return false;

    const birthDate = new Date(birthday);
    if (Number.isNaN(birthDate.getTime())) return false;

    const today = new Date();
    const schoolYearStart = new Date(today.getFullYear(), SCHOOL_YEAR_START_MONTH - 1, 1);
    const schoolYear = today >= schoolYearStart ? today.getFullYear() : today.getFullYear() - 1;
    const afterCutoff = birthDate.getMonth() > 8 || (birthDate.getMonth() === 8 && birthDate.getDate() > 1);
    const entryYear = birthDate.getFullYear() + 6 + (afterCutoff ? 1 : 0);
    const expectedGrade = schoolYear - entryYear + 1;

    return Math.abs(expectedGrade - gradeNum) > 1;
};

// 季度選項採用固定代碼存 DB/API，text 只負責畫面顯示。
// 例如資料庫存 'summer' 時，前台會顯示為「暑期」。
export const normalizeCampSeasonType = (value) => {
    const text = String(value || '').trim().toLowerCase();
    const map = {
        summer: 'summer',
        '暑期': 'summer',
        '暑假': 'summer',
        winter: 'winter',
        '寒假': 'winter',
        other: 'other',
        '其他': 'other',
    };

    return map[text] || text;
};

export const findCampSeasonType = (options, value) => {
    const normalized = normalizeCampSeasonType(value);
    return (options || []).find((item) => {
        return normalizeCampSeasonType(item.value || item.Value) === normalized ||
            normalizeCampSeasonType(item.text || item.Text) === normalized;
    });
};

export const getCampSeasonText = (value, options = []) => {
    const item = findCampSeasonType(options, value);
    return item ? (item.text || item.Text) : (value || '-');
};
